import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import API from '../../services/api';
import MobileDialog from './MobileDialog';

const DEFAULT_STATUS_OPTIONS = [
  { value: 'pending', label: 'בתהליך' },
  { value: 'processing', label: 'בטיפול' },
  { value: 'completed', label: 'הושלם' },
  { value: 'cancelled', label: 'בוטל' },
];

export default function OrderStatusDialog({
  open,
  onClose,
  order,
  onSuccess,
  orderFieldsSchema = {},
  orderLabel = 'הזמנה',
}) {
  const queryClient = useQueryClient();
  const [newStatus, setNewStatus] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (open) {
      setNewStatus(order?.Status || '');
      setErrorMessage('');
    }
  }, [open, order]);

  const isCancelled = order?.Status === 'cancelled';

  const mutation = useMutation({
    mutationFn: async (status) => {
      const response = await API.post('/order/status/', {
        OrderID: order.OrderID,
        Status: status,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['orders']);
      queryClient.invalidateQueries(['order', order?.OrderID]);
      onSuccess?.();
      onClose?.();
    },
    onError: (error) => {
      setErrorMessage(error?.response?.data?.error || 'שגיאה בעדכון הסטטוס');
    },
  });

  const handleSubmit = () => {
    if (!newStatus) {
      setErrorMessage('יש לבחור סטטוס');
      return;
    }
    mutation.mutate(newStatus);
  };

  const statusOptions = (orderFieldsSchema.Status?.options || DEFAULT_STATUS_OPTIONS)
    .filter((option) => option.value !== 'cancelled');

  return (
    <MobileDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>עדכון סטטוס {orderLabel}</DialogTitle>
      <DialogContent>
        {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}
        {isCancelled ? (
          <Alert severity="warning">לא ניתן לשנות סטטוס של {orderLabel} מבוטלת</Alert>
        ) : (
          <TextField
            select
            label="סטטוס"
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
            fullWidth
            margin="normal"
          >
            {statusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>ביטול</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={mutation.isLoading || isCancelled}
        >
          {mutation.isLoading ? 'מעדכן...' : 'עדכן'}
        </Button>
      </DialogActions>
    </MobileDialog>
  );
}
